/**
 * Página da Política de Privacidade (/privacidade)
 *
 * Enquanto os dados da empresa em company.ts não estiverem completos, a
 * página mostra um aviso de rascunho e pede aos buscadores para não indexar.
 *
 * @author Equipe VendeAi
 * @since 1.4.0
 */

import { useEffect } from 'react';
import { ShieldCheck } from 'lucide-react';
import { usePageMeta } from '../usePageMeta';
import { Block, PageShell } from '../blog/BlogPages';
import { LEGAL_COMPANY, PRIVACY_PATH, PRIVACY_POLICY_READY } from './company';
import { PRIVACY_DESCRIPTION, PRIVACY_TITLE, privacyPolicyBlocks } from './privacyPolicy';

/** Rascunho não deve aparecer nos buscadores */
function useNoIndex(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [active]);
}

const formatDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

export function PrivacyPage() {
  usePageMeta(PRIVACY_TITLE, PRIVACY_DESCRIPTION, PRIVACY_PATH);
  useNoIndex(!PRIVACY_POLICY_READY);

  return (
    <PageShell>
      <article className="mx-auto max-w-3xl px-4 pb-24 pt-28 sm:px-6 sm:pt-36 lg:px-8">
        {!PRIVACY_POLICY_READY && (
          <p
            role="note"
            className="mb-8 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-amber-100"
          >
            <strong>Rascunho em revisão.</strong> Este texto ainda não está em vigor: faltam os
            dados da empresa e a revisão jurídica.
          </p>
        )}
        <span className="inline-flex items-center gap-2 rounded-full border border-violet-400/25 bg-violet-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-violet-300">
          <ShieldCheck size={14} aria-hidden /> LGPD
        </span>
        <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
          Política de Privacidade
        </h1>
        {LEGAL_COMPANY.effectiveDate && (
          <p className="mt-4 text-sm text-slate-400">
            Versão em vigor desde{' '}
            <time dateTime={LEGAL_COMPANY.effectiveDate}>
              {formatDate(LEGAL_COMPANY.effectiveDate)}
            </time>
          </p>
        )}
        <div className="mt-8 border-t border-white/10 pt-2 text-base leading-relaxed text-slate-300">
          {privacyPolicyBlocks().map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </div>
      </article>
    </PageShell>
  );
}
